/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/


import React from 'react';
import { motion } from 'framer-motion';
import { Professeur } from '../type';
import { ArrowUpRight } from 'lucide-react';

interface ProfesseurCardProps {
  Professeur: Professeur;
  onClick: () => void;
}

const ProfesseurCard: React.FC<ProfesseurCardProps> = ({ Professeur, onClick }) => {
  return (
    <motion.div
      className="group relative h-[400px] md:h-[500px] w-full overflow-hidden border-b md:border-r border-white/10 bg-black cursor-pointer"
      initial="rest"
      whileHover="hover"
      whileTap="hover"
      animate="rest"
      data-hover="true"
      onClick={onClick}
    >
      {/* Image Background with Zoom */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.img
          src={Professeur.image}
          alt={Professeur.name}
          className="h-full w-full object-cover grayscale will-change-transform"
          variants={{
            rest: { scale: 1, opacity: 0.6, filter: 'grayscale(100%)' },
            hover: { scale: 1.05, opacity: 0.9, filter: 'grayscale(0%)' }
          }}
          transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
        />
        <div className="absolute inset-0 bg-black/30 group-hover:bg-[#637ab9]/20 transition-colors duration-500" />
      </div>

      {/* Overlay Info */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between pointer-events-none">
        <div className="flex justify-between items-start">
          <a
            href={Professeur.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs font-mono border border-white/30 px-3 py-1.5 rounded-full backdrop-blur-md pointer-events-auto hover:bg-white hover:text-black transition-colors text-white inline-block hover:scale-105 active:scale-95"
          >
            {Professeur.username}
          </a>
          <motion.div
            variants={{
              rest: { opacity: 0, x: 20, y: -20 },
              hover: { opacity: 1, x: 0, y: 0 }
            }}
            className="bg-white text-black rounded-full p-2 will-change-transform"
          >
            <ArrowUpRight className="w-6 h-6" />
          </motion.div>
        </div>

        <div>
          <div className="overflow-hidden">
            <motion.h3
              className="font-heading text-3xl md:text-4xl font-bold uppercase text-white mix-blend-difference will-change-transform"
              variants={{
                rest: { y: 0 },
                hover: { y: -5 }
              }}
              transition={{ duration: 0.4 }}
            >
              {Professeur.name}
            </motion.h3>
          </div>
          <motion.p
            className="text-sm font-medium uppercase tracking-widest text-[#4fb7b3] mt-2 will-change-transform"
            variants={{
              rest: { opacity: 0, y: 10 },
              hover: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {Professeur.matiere}
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
};

export default ProfesseurCard;